import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate, COMPANY } from "@/lib/helpers";
import { DetailShell } from "@/components/DetailShell";
import {
  BtnPrimary, BtnSecondary, PageHeader, Field, Input, Select, Textarea, Empty,
} from "@/components/primitives";
import PhotoUploader from "@/components/PhotoUploader";
import SmartInput from "@/components/SmartInput";
import MicDictate from "@/components/MicDictate";
import { Plus, Trash, Wrench, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const LABEL = {
  headlight: "Headlight", brakelight: "Brakelight", parklight: "Park Light",
  gauges: "Gauges", air_filters: "Air Filters", oil_filters: "Oil Filters",
  fuel_filters: "Fuel Filters", handbrake: "Handbrake", acceleration: "Acceleration",
  tyres_trackshoes: "Tyres / Track shoes", paddle_switch: "Paddle / Switch",
  wipers: "Wipers", air_leaks: "Air Leaks", horn: "Horn", reverse_alarm: "Reverse Alarm",
  major_leaks: "Major Fuel/Oil/Water Leaks", filters: "Filters (General)",
  temperatures: "Temperatures", fire_suppression: "Fire Suppression System",
  coolant_levels: "Coolant Levels", isolator: "Isolator", seatbelts: "Seatbelts",
  warning_systems: "Warning Systems", battery_condition: "Battery Condition",
};

function StatusRadio({ name, value, onChange }) {
  const opts = ["ok", "yes", "no", "na"];
  const colors = { ok: "#00A142", yes: "#002FA7", no: "#FF2A2A", na: "#a1a1aa" };
  return (
    <div className="flex gap-1.5">
      {opts.map((o) => (
        <button
          type="button"
          key={o}
          onClick={() => onChange(o)}
          data-testid={`${name}-${o}`}
          className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border transition-all ${
            value === o
              ? "text-white border-transparent"
              : "text-[#52525b] border-[#e4e4e7] hover:border-[#09090b]"
          }`}
          style={value === o ? { background: colors[o] } : {}}
        >
          {o.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function MaintenanceRecord() {
  const nav = useNavigate();
  const { id } = useParams();
  const isView = !!id;
  const [machines, setMachines] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [form, setForm] = useState({
    machine_id: "",
    equipment_fleet_no: "",
    mechanic_name: "",
    mine_no: "",
    date: new Date().toISOString().slice(0, 10),
    mileage: "",
    next_service_mileage: "",
    service_type: "routine",
    service_category: "",
    checklist: {},
    comments: "",
    defects: "",
    replacements: [{ description: "", reason: "" }],
    mechanic_signature: null,
    supervisor_signature: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cl }, { data: mach }] = await Promise.all([
          api.get("/maintenance-records/checklist-items"),
          api.get("/machines"),
        ]);
        setItems(cl.items || []);
        setMeta(cl.doc_meta || {});
        setMachines(mach || []);
        try {
          const { data: sc } = await api.get("/lookups/service_category");
          setServiceCategories(sc || []);
        } catch (_e) { /* non-fatal */ }
      } catch (e) { toast.error(formatError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!isView) return;
    (async () => {
      try {
        const { data } = await api.get(`/maintenance-records/${id}`);
        // Coalesce nullable string fields to "" so controlled Inputs never
        // switch between controlled and uncontrolled.
        const STRING_FIELDS = [
          "machine_id", "equipment_fleet_no", "mechanic_name", "mine_no",
          "date", "service_type", "service_category", "comments", "defects",
          "mechanic_signature", "supervisor_signature",
        ];
        const NUMBER_STRING_FIELDS = ["mileage", "next_service_mileage"];
        const clean = { ...data };
        for (const k of STRING_FIELDS) clean[k] = clean[k] ?? "";
        for (const k of NUMBER_STRING_FIELDS) {
          clean[k] = clean[k] === null || clean[k] === undefined ? "" : clean[k];
        }
        clean.checklist = clean.checklist || {};
        clean.replacements = data.replacements?.length
          ? data.replacements
          : [{ description: "", reason: "" }];
        setForm(clean);
      } catch (e) { toast.error(formatError(e)); }
    })();
  }, [id, isView]);

  const leftItems = useMemo(() => items.slice(0, 12), [items]);
  const rightItems = useMemo(() => items.slice(12), [items]);

  function setChk(key, val) {
    setForm({ ...form, checklist: { ...form.checklist, [key]: val } });
  }

  async function submit() {
    if (!form.equipment_fleet_no || !form.mechanic_name || !form.date) {
      toast.error("Fleet No., Mechanic Name and Date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        mileage: form.mileage === "" ? null : Number(form.mileage),
        next_service_mileage: form.next_service_mileage === "" ? null : Number(form.next_service_mileage),
        replacements: (form.replacements || []).filter((r) => r.description || r.reason),
      };
      const { data } = await api.post("/maintenance-records", payload);
      toast.success(`Saved maintenance record for ${data.equipment_fleet_no}`);
      nav("/maintenance-records");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  return (
    <DetailShell
      overline="MAINTENANCE / DIGITAL FORM"
      title="Equipment Maintenance Record"
      subtitle={`${COMPANY.name} · ${meta.doc_no || "OHSE.FM-PZO-098"}`}
      backTo="/maintenance-records"
      testId="maint-form"
      actions={
        !isView && (
          <>
            <BtnSecondary onClick={() => nav(-1)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={submit} disabled={saving} data-testid="maint-save">
              <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save Record"}
            </BtnPrimary>
          </>
        )
      }
    >
      {/* Record Information */}
      <div className="phizo-card p-5 mb-6">
        <div className="phizo-label mb-4">Record Information</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Mechanic Name *">
            <Input
              value={form.mechanic_name}
              onChange={(e) => setForm({ ...form, mechanic_name: e.target.value })}
              data-testid="maint-mechanic"
              disabled={isView}
            />
          </Field>
          <Field label="Mine No.">
            <Input value={form.mine_no || ""} onChange={(e) => setForm({ ...form, mine_no: e.target.value })} disabled={isView} />
          </Field>
          <Field label="Machine (link)">
            <Select
              value={form.machine_id || ""}
              onChange={(e) => {
                const m = machines.find((x) => x.id === e.target.value);
                setForm({
                  ...form,
                  machine_id: e.target.value,
                  equipment_fleet_no: m?.asset_tag || form.equipment_fleet_no,
                });
              }}
              disabled={isView}
            >
              <option value="">— Manual entry —</option>
              {machines.map((m) => (<option key={m.id} value={m.id}>{`${m.asset_tag} · ${m.name}`}</option>))}
            </Select>
          </Field>
          <Field label="Equipment Fleet No. *">
            <Input
              value={form.equipment_fleet_no}
              onChange={(e) => setForm({ ...form, equipment_fleet_no: e.target.value })}
              data-testid="maint-fleet"
              disabled={isView}
            />
          </Field>
          <Field label="Date *">
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="maint-date" disabled={isView} />
          </Field>
          <Field label="Service Type">
            <Select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="maint-type" disabled={isView}>
              <option value="routine">Routine (Due for service)</option>
              <option value="breakdown">Breakdown (Unplanned failure)</option>
            </Select>
          </Field>
          <Field label="Service Category" hint="Pick from master list or type a new one">
            <SmartInput
              testId="maint-service-category"
              value={form.service_category || ""}
              onChange={(v) => setForm({ ...form, service_category: v })}
              options={serviceCategories.map((s) => ({ value: s.name, hint: s.code || "" }))}
              placeholder="e.g. Oil Change, Hydraulics…"
              disabled={isView}
            />
          </Field>
          <Field label="Mileage / Hours">
            <Input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} disabled={isView} />
          </Field>
          <Field label="Next Service Mileage">
            <Input type="number" value={form.next_service_mileage} onChange={(e) => setForm({ ...form, next_service_mileage: e.target.value })} disabled={isView} />
          </Field>
        </div>
      </div>

      {/* Physical Verification Checklist */}
      <div className="phizo-card p-5 mb-6">
        <div className="phizo-label mb-4">
          Physical Verification Checklist — Tick as per corresponding status
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2">
          {[leftItems, rightItems].map((col, ci) => (
            <div key={ci} className="space-y-1">
              {col.map((k, idx) => (
                <div
                  key={k}
                  className="flex items-center justify-between border-b border-[#f4f4f5] py-2"
                >
                  <div className="text-sm">
                    <span className="mono text-xs text-[#a1a1aa] mr-2">
                      {(ci * 12 + idx + 1).toString().padStart(2, "0")}.
                    </span>
                    {LABEL[k] || k}
                  </div>
                  <StatusRadio
                    name={`chk-${k}`}
                    value={form.checklist?.[k] || ""}
                    onChange={(v) => setChk(k, v)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Comments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="phizo-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="phizo-label">Comments / Findings</div>
            {!isView && (
              <MicDictate
                context="truck maintenance comments; mining; mechanical"
                onTranscript={(text) =>
                  setForm((f) => ({ ...f, comments: (f.comments ? f.comments + " " : "") + text }))
                }
                testId="maint-comments-mic"
              />
            )}
          </div>
          <Textarea rows={4} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} data-testid="maint-comments" disabled={isView} />
        </div>
        <div className="phizo-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="phizo-label">Defects</div>
            {!isView && (
              <MicDictate
                context="truck defects list; mechanical"
                onTranscript={(text) =>
                  setForm((f) => ({ ...f, defects: (f.defects ? f.defects + " " : "") + text }))
                }
                testId="maint-defects-mic"
              />
            )}
          </div>
          <Textarea rows={4} value={form.defects} onChange={(e) => setForm({ ...form, defects: e.target.value })} data-testid="maint-defects" disabled={isView} />
        </div>
      </div>

      {/* Replacements */}
      <div className="phizo-card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="phizo-label">Component Replacement Information</div>
          {!isView && (
            <BtnSecondary
              onClick={() => setForm({ ...form, replacements: [...form.replacements, { description: "", reason: "" }] })}
              data-testid="maint-add-replacement"
            >
              <Plus size={12} weight="bold" /> Row
            </BtnSecondary>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="phizo-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Item Description</th>
                <th>Reason for Replacement</th>
                {!isView && <th style={{ width: 40 }}></th>}
              </tr>
            </thead>
            <tbody>
              {form.replacements.map((r, i) => (
                <tr key={i}>
                  <td className="mono text-xs text-[#a1a1aa]">{i + 1}</td>
                  <td>
                    <Input
                      value={r.description}
                      onChange={(e) => {
                        const next = [...form.replacements];
                        next[i] = { ...next[i], description: e.target.value };
                        setForm({ ...form, replacements: next });
                      }}
                      data-testid={`maint-rep-desc-${i}`}
                      disabled={isView}
                    />
                  </td>
                  <td>
                    <Input
                      value={r.reason}
                      onChange={(e) => {
                        const next = [...form.replacements];
                        next[i] = { ...next[i], reason: e.target.value };
                        setForm({ ...form, replacements: next });
                      }}
                      data-testid={`maint-rep-reason-${i}`}
                      disabled={isView}
                    />
                  </td>
                  {!isView && (
                    <td className="text-right">
                      <button
                        onClick={() => setForm({ ...form, replacements: form.replacements.filter((_, j) => j !== i) })}
                        className="text-[#a1a1aa] hover:text-[#FF2A2A] p-1"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="phizo-card p-4">
          <div className="phizo-label mb-3">Mechanic Signature</div>
          {isView && form.mechanic_signature ? (
            <img src={form.mechanic_signature} alt="Mechanic sig" className="max-h-32 border" />
          ) : (
            <PhotoUploader
              value={form.mechanic_signature}
              onChange={(url) => setForm({ ...form, mechanic_signature: url })}
              category="signatures"
              size={128}
              testId="maint-sig-mechanic"
            />
          )}
        </div>
        <div className="phizo-card p-4">
          <div className="phizo-label mb-3">Supervisor Signature</div>
          {isView && form.supervisor_signature ? (
            <img src={form.supervisor_signature} alt="Supervisor sig" className="max-h-32 border" />
          ) : (
            <PhotoUploader
              value={form.supervisor_signature}
              onChange={(url) => setForm({ ...form, supervisor_signature: url })}
              category="signatures"
              size={128}
              testId="maint-sig-supervisor"
            />
          )}
        </div>
      </div>

      {/* Document control footer */}
      <div className="phizo-card p-4 text-[10px] text-[#52525b] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <div><div className="phizo-label">Doc No.</div>{meta.doc_no || "OHSE.FM-PZO-098"}</div>
        <div><div className="phizo-label">Owner</div>{meta.doc_owner || "Maintenance Dept"}</div>
        <div><div className="phizo-label">Authorised By</div>{meta.authorized_by || "LR McCallum"}</div>
        <div><div className="phizo-label">Approved By</div>{meta.approved_by || "Misozi Phiri"}</div>
        <div><div className="phizo-label">Rev Date</div>{fmtDate(meta.rev_date)}</div>
        <div><div className="phizo-label">Next Rev</div>{fmtDate(meta.next_rev)}</div>
      </div>
    </DetailShell>
  );
}

export function MaintenanceRecordsList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/maintenance-records"); setRows(data || []); }
      catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        title="Maintenance Records"
        subtitle="Digital equipment maintenance forms (OHSE.FM-PZO-098)"
        actions={
          <BtnPrimary onClick={() => nav("/maintenance-records/new")} data-testid="maint-new">
            <Wrench size={12} weight="bold" /> New Record
          </BtnPrimary>
        }
      />
      {loading ? (
        <div className="phizo-label">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty label="No maintenance records yet." />
      ) : (
        <div className="phizo-card overflow-x-auto">
          <table className="phizo-table">
            <thead>
              <tr>
                <th>Date</th><th>Fleet No.</th><th>Mechanic</th><th>Type</th>
                <th className="text-right">Mileage</th><th>Logged By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`maint-row-${r.equipment_fleet_no}`}
                  onClick={() => nav(`/maintenance-records/${r.id}`)}
                  className="cursor-pointer hover:bg-[#f4f4f5]"
                >
                  <td className="text-sm">{fmtDate(r.date)}</td>
                  <td className="mono font-semibold">{r.equipment_fleet_no}</td>
                  <td>{r.mechanic_name}</td>
                  <td>
                    <span className="phizo-status" style={{
                      background: r.service_type === "breakdown" ? "#FF2A2A" : "#00A142",
                      color: "#fff", borderColor: "transparent"
                    }}>
                      {r.service_type}
                    </span>
                  </td>
                  <td className="text-right mono">{r.mileage != null ? Number(r.mileage).toLocaleString() : "—"}</td>
                  <td className="text-sm text-[#52525b]">{r.logged_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
